import { useState, useEffect } from "react";
import { Table, Button, Checkbox, message } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { hackathonApi } from "../services/hackathon";
import { resourceApi, type Resource } from "../services/resources";
import { ArrowLeftOutlined } from "@ant-design/icons";
import Pagination from "../components/Pagination";

const AssignResourcesToHackathon = () => {
  const { hackathonId } = useParams<{ hackathonId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch hackathon details
  const { data: hackathonData, isLoading: hackathonLoading } = useQuery({
    queryKey: ["hackathon", hackathonId],
    queryFn: () => hackathonApi.getHackathonById(hackathonId!),
    enabled: !!hackathonId,
  });

  // Fetch all general resources with pagination
  const { data: resourcesData, isLoading: resourcesLoading } = useQuery({
    queryKey: ["general-resources", currentPage, pageSize],
    queryFn: () => resourceApi.getResources({ 
      page: currentPage, 
      limit: pageSize 
    }),
    select: (data) => data.success ? data : { data: [], pagination: { totalItems: 0 } },
  });

  // Fetch already assigned resources for this hackathon
  const { data: assignedResourcesData } = useQuery({
    queryKey: ["hackathon-resources", hackathonId],
    queryFn: () => hackathonApi.getHackathonResources(hackathonId!),
    enabled: !!hackathonId,
    select: (data) => {
      if (data.success && data.data && Array.isArray(data.data.resources)) {
        return data.data.resources.map((resource: any) => resource._id);
      }
      return [];
    },
  });

  // Assign resources mutation
  const assignMutation = useMutation({
    mutationFn: (resourceIds: string[]) =>
      hackathonApi.assignResourcesToHackathon(hackathonId!, resourceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["hackathon-resources", hackathonId],
      });
      message.success("Resources assigned successfully!");
      navigate("/resources");
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.message || "Failed to assign resources"
      );
    },
  });

  // Initialize selected resources with already assigned ones
  useEffect(() => {
    if (assignedResourcesData) {
      setSelectedResources(assignedResourcesData);
    }
  }, [assignedResourcesData]);

  const handleResourceToggle = (resourceId: string) => {
    setSelectedResources((prev) =>
      prev.includes(resourceId)
        ? prev.filter((id) => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  const handleSelectAll = () => {
    if (!resourcesData?.data) return;

    const allResourceIds = resourcesData.data.map(
      (resource: Resource) => resource._id
    );
    setSelectedResources(allResourceIds);
  };

  const handleDeselectAll = () => {
    setSelectedResources([]);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSaveAssignment = () => {
    if (selectedResources.length === 0) {
      message.warning("Please select at least one resource");
      return;
    }
    assignMutation.mutate(selectedResources);
  };

  const columns = [
    {
      title: "",
      key: "select",
      width: 50,
      render: (record: Resource) => (
        <Checkbox
          checked={selectedResources.includes(record._id)}
          onChange={() => handleResourceToggle(record._id)}
        />
      ),
    },
    {
      title: "Resource Title",
      dataIndex: "title",
      key: "title",
      render: (text: string) => (
        <span className="font-medium text-gray-900">{text}</span>
      ),
    },
    {
      title: "Category",
      dataIndex: "categoryId",
      key: "category",
      render: (categoryId: any) => (
        <span className="text-gray-700">
          {typeof categoryId === "object" ? categoryId.name : "Unknown"}
        </span>
      ),
    },
    {
      title: "Resource Type",
      dataIndex: "resourceType",
      key: "resourceType",
      render: (type: string) => (
        <span className="text-gray-700 capitalize">{type}</span>
      ),
    },
  ];

  if (hackathonLoading || resourcesLoading) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-6">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/resources/assign-resources")}
            className="text-gray-600 hover:text-gray-900 p-2 h-auto font-medium"
          >
            Back
          </Button>
        </div>

        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">
            Select Resources for{" "}
            {hackathonData?.data?.title || hackathonData?.data?.hackathon?.title || "Hackathon"}
          </h1>

          <div className="flex items-center gap-3">
            {/* <Button 
              onClick={() => navigate('/resources/assign-resources')}
              className="h-9 !text-sm !rounded-[10px]"
            >
              Back
            </Button> */}

            <Button
              type="primary"
              onClick={handleSaveAssignment}
              loading={assignMutation.isPending}
              className="bg-primary hover:bg-primary/90 h-9 !text-sm !rounded-[10px]"
            >
              Save Assignment
            </Button>
          </div>
        </div>
      </div>

      {/* Resources Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 flex flex-col">
        <Table
          columns={columns}
          dataSource={
            resourcesData?.data?.map((resource) => ({
              ...resource,
              key: resource._id,
            })) || []
          }
          pagination={false}
          className="hackathon-table flex-1"
          rowClassName="hover:bg-gray-50"
        />
        
        {/* Custom Pagination */}
        {resourcesData && resourcesData.pagination && resourcesData.pagination.totalItems > 0 && (
          <div className="border-t border-gray-200 p-4">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(resourcesData.pagination.totalItems / pageSize)}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignResourcesToHackathon;
